<template>
  <div class="content-wrapper view-discount-manage p-0">
    <div class="content">
      <div class="card">
        <div class="card-body">
          <h4><i class="fas fa-percent"></i> จัดการส่วนลด</h4>
          <div class="row mx-1 my-3">
            <b-form-input
              type="search"
              class="col required"
              autofocus
              v-model="filter"
              placeholder="ค้นหาส่วนลดจากชื่อ"
            ></b-form-input>
            <b-button
              class="col-5 col-sm-4 col-md-3 ml-3"
              variant="primary"
              @click="openCreateModal"
            >
              <i class="fas fa-plus"></i> เพิ่มส่วนลด
            </b-button>
          </div>

          <b-table
            id="discountManageTable"
            ref="discountManageTable"
            hover
            small
            sort-icon-left
            head-variant="light"
            :items="discountList"
            :fields="fields"
            :filter="filter"
            responsive
            table-class="compact-list-table"
            show-empty
            empty-text="ไม่มีรายการส่วนลด"
          >
            <template v-slot:cell(index)="row">{{ row.index + 1 }}.</template>
            <template #cell(code)="row">
              <code v-if="row.item.code" class="small">{{ row.item.code }}</code>
              <span v-else class="text-muted">-</span>
            </template>
            <template #cell(scope)="row">
              <b-badge :variant="row.item.scope === 'RECEIPT' ? 'info' : 'light'" class="text-dark">
                {{ row.item.scope === "RECEIPT" ? "ใบเสร็จ" : "รายการ" }}
              </b-badge>
            </template>
            <template #cell(dateRange)="row">
              <div v-if="!row.item.startDt && !row.item.endDt" class="text-muted small">ตลอด</div>
              <div v-else class="small">
                <div>{{ formatDate(row.item.startDt) || '...' }} – {{ formatDate(row.item.endDt) || '...' }}</div>
                <b-badge
                  v-if="getDateStatus(row.item)"
                  :variant="getDateStatus(row.item).variant"
                  pill
                  class="mt-1"
                  style="font-size: 0.65rem"
                >
                  {{ getDateStatus(row.item).text }}
                </b-badge>
              </div>
            </template>
            <template #cell(usage)="row">
              <span v-if="!row.item.maxUsage" class="text-muted small">ไม่จำกัด</span>
              <span v-else class="small">{{ row.item.usageCount || 0 }}/{{ row.item.maxUsage }}</span>
            </template>
            <template #cell(discountType)="row">
              {{ row.item.discountType === 0 ? "เปอร์เซ็นต์" : "บาท" }}
            </template>
            <template #cell(discountValue)="row">
              <template v-if="row.item.discountMethod === 1">
                DF: {{ row.item.discountType === 0 ? `${row.item.doctorDiscountValue}%` : `${row.item.doctorDiscountValue} บาท` }}
                / CF: {{ row.item.discountType === 0 ? `${row.item.clinicDiscountValue}%` : `${row.item.clinicDiscountValue} บาท` }}
              </template>
              <template v-else>
                {{
                  row.item.discountType === 0
                    ? `${row.item.discountValue}%`
                    : `${row.item.discountValue} บาท`
                }}
              </template>
            </template>
            <template #cell(discountMethod)="row">
              {{ row.item.discountMethod === 0 ? "ทั่วไป" : "กำหนดเอง (DF/CF)" }}
            </template>
            <template #cell(treatments)="row">
              <span v-if="!row.item.treatments || row.item.treatments.length === 0" class="text-muted">
                ทุกรายการ
              </span>
              <span v-else v-b-tooltip.hover.top="getTreatmentTooltip(row.item.treatments)" class="info-badge">
                {{ row.item.treatments.length }} รายการ
                <i class="fas fa-info-circle text-muted small"></i>
              </span>
            </template>
            <template #cell(branchIds)="row">
              <span v-if="!row.item.branchIds || row.item.branchIds.length === 0" class="text-muted">
                ทุกสาขา
              </span>
              <span v-else v-b-tooltip.hover.top="getBranchTooltip(row.item.branchIds)" class="info-badge">
                {{ row.item.branchIds.length }} สาขา
                <i class="fas fa-info-circle text-muted small"></i>
              </span>
            </template>
            <template #cell(isActive)="row">
              <b-badge :variant="row.item.isActive ? 'success' : 'secondary'">
                {{ row.item.isActive ? "เปิดใช้" : "ปิดใช้" }}
              </b-badge>
            </template>
            <template v-slot:cell(buttons)="row">
              <b-button-group class="show-when-hovered" size="xs">
                <b-button
                  variant="outline-secondary"
                  @click="openEditModal(row.item)"
                >
                  <i class="fas fa-edit"></i>
                </b-button>
                <b-button
                  variant="outline-danger"
                  @click="confirmDelete(row.item)"
                >
                  <i class="fas fa-trash-alt"></i>
                </b-button>
              </b-button-group>
            </template>
          </b-table>
        </div>
      </div>
      <DiscountForm
        ref="discountForm"
        :clinic-type="clinicType"
        @saved="fetchDiscounts"
      />
      <Loading v-if="isLoading"></Loading>
      <Dialog ref="Dialog" />
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import DiscountForm from "@/components/discount/DiscountForm.vue";
import Loading from "@/components/Loading";
import Dialog from "@/components/modal/Dialog.vue";

export default {
  name: "DiscountManagement",
  components: { DiscountForm, Loading, Dialog },
  data() {
    return {
      filter: "",
      isLoading: false,
      fields: [
        { key: "index", label: "#", class: "text-center col-dm-index" },
        { key: "code", label: "รหัส", class: "col-dm-code" },
        { key: "name", label: "ชื่อส่วนลด", sortable: true, class: "col-dm-name" },
        { key: "scope", label: "ขอบเขต", class: "col-dm-scope" },
        { key: "dateRange", label: "ช่วงเวลา", class: "col-dm-date" },
        { key: "usage", label: "ใช้แล้ว", class: "col-dm-usage text-center" },
        { key: "discountMethod", label: "ประเภท", class: "col-dm-type" },
        { key: "discountValue", label: "มูลค่า", class: "col-dm-value" },
        { key: "treatments", label: "รายการรักษา", class: "col-dm-treat" },
        { key: "branchIds", label: "สาขา", class: "col-dm-branch" },
        { key: "isActive", label: "สถานะ", class: "col-dm-status" },
        { key: "buttons", label: "จัดการ", class: "text-center col-dm-buttons" },
      ],
    };
  },
  computed: {
    ...mapGetters("moduleDiscount", ["getDiscountList"]),
    ...mapGetters("moduleAppointment", ["getTreatList", "getTreatCateList", "getItemList"]),
    ...mapGetters("moduleUser", ["getClinicList", "getBranchInfo"]),
    discountList() {
      return this.getDiscountList;
    },
    clinicUrl() {
      return this.$route.params.clinicUrl;
    },
    branchUrl() {
      return this.$route.params.branchUrl;
    },
    clinicType() {
      return this.$store.state.moduleBranch?.branchInfo?.clinic?.type || "STANDALONE";
    },
    branchMap() {
      if (!this.getClinicList || !this.getBranchInfo) return {};
      const clinic = this.getClinicList.find(c => c.id === this.getBranchInfo?.clinic?.id);
      if (!clinic || !clinic.branches) return {};
      const map = {};
      clinic.branches.forEach(b => { map[b.id] = b.name; });
      return map;
    },
  },
  mounted() {
    this.fetchDiscounts();
    this.fetchMasterData();
  },
  methods: {
    ...mapActions("moduleDiscount", ["fetchDiscountList", "deleteDiscount"]),
    ...mapActions("moduleAppointment", ["fetchTreatList", "fetchTreatCateList", "fetchItemList"]),
    async fetchMasterData() {
      const payload = { clinicUrl: this.clinicUrl, branchUrl: this.branchUrl };
      await Promise.all([
        this.fetchTreatList(payload).catch(() => {}),
        this.fetchTreatCateList(payload).catch(() => {}),
        this.fetchItemList(payload).catch(() => {}),
      ]);
    },
    getDateStatus(item) {
      if (!item.startDt && !item.endDt) return null;
      const now = new Date();
      if (item.endDt && new Date(item.endDt) < now) return { text: "หมดอายุ", variant: "danger" };
      if (item.startDt && new Date(item.startDt) > now) return { text: "ยังไม่เริ่ม", variant: "warning" };
      return { text: "ใช้งานอยู่", variant: "success" };
    },
    formatDate(dt) {
      if (!dt) return "";
      return new Date(dt).toLocaleDateString("th-TH");
    },
    async fetchDiscounts() {
      this.isLoading = true;
      await this.fetchDiscountList({
        clinicUrl: this.clinicUrl,
        branchUrl: this.branchUrl,
      });
      this.isLoading = false;
    },
    formatTreatmentLabel(item) {
      if (item.treatCateId) {
        const cate = (this.getTreatCateList || []).find(c => c.id === item.treatCateId);
        return cate ? `[หมวด] ${cate.nameTh}` : `หมวด #${item.treatCateId}`;
      }
      if (item.treatId) {
        const treat = (this.getTreatList || []).find(t => t.id === item.treatId);
        return treat ? treat.nameTh : `รักษา #${item.treatId}`;
      }
      if (item.itemId) {
        const itm = (this.getItemList || []).find(i => i.id === item.itemId);
        return itm ? `[สินค้า] ${itm.nameTh}` : `สินค้า #${item.itemId}`;
      }
      return "";
    },
    getTreatmentTooltip(treatments) {
      return treatments.map(t => `• ${this.formatTreatmentLabel(t)}`).join("\n");
    },
    getBranchTooltip(branchIds) {
      return branchIds.map(id => `• ${this.branchMap[id] || `สาขา #${id}`}`).join("\n");
    },
    openCreateModal() {
      this.$refs.discountForm.show({
        clinicUrl: this.clinicUrl,
        branchUrl: this.branchUrl,
      });
    },
    openEditModal(discount) {
      this.$refs.discountForm.show({
        clinicUrl: this.clinicUrl,
        branchUrl: this.branchUrl,
        discount,
      });
    },
    async confirmDelete(discount) {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        `ต้องการลบส่วนลด "${discount.name}" ?`,
        { title: "ยืนยันการลบ", okVariant: "danger", okTitle: "ลบ", cancelTitle: "ยกเลิก" }
      );
      if (confirmed) {
        try {
          this.isLoading = true;
          await this.deleteDiscount({
            clinicUrl: this.clinicUrl,
            branchUrl: this.branchUrl,
            id: discount.id,
          });
          this.$refs.Dialog.showToast("success", "ลบส่วนลดสำเร็จ");
          await this.fetchDiscounts();
        } catch {
          // ErrorService handles error toast automatically via axios interceptor
          this.isLoading = false;
        }
      }
    },
  },
};
</script>

<style lang="scss">
.view-discount-manage {
  // table-layout: fixed ทำให้ min-width ไม่ทำงาน ต้องใช้ auto
  .compact-list-table {
    table-layout: auto;
  }

  .col-dm-index   { width: 35px; white-space: nowrap; }
  .col-dm-code    { width: 80px; white-space: nowrap; }
  .col-dm-name    { min-width: 200px; white-space: normal; word-break: break-word; }
  .col-dm-scope   { width: 70px; white-space: nowrap; }
  .col-dm-date    { width: 80px; white-space: nowrap; }
  .col-dm-usage   { width: 60px; white-space: nowrap; }
  .col-dm-type    { width: 85px; white-space: nowrap; }
  .col-dm-value   { width: 110px; white-space: normal; }
  .col-dm-treat   { width: 60px; white-space: nowrap; }
  .col-dm-branch  { width: 55px; white-space: nowrap; }
  .col-dm-status  { width: 55px; white-space: nowrap; }
  .col-dm-buttons { width: 75px; white-space: nowrap; }

  .info-badge {
    cursor: pointer;
    text-decoration: underline dotted;
    &:hover {
      color: #007bff;
    }
  }
}

@media (max-width: 991.98px) {
  .view-discount-manage {
    .col-dm-name   { min-width: 160px; }
    .col-dm-value  { width: 95px; }
  }
}

@media (max-width: 767.98px) {
  .view-discount-manage {
    .col-dm-name   { min-width: 130px; }
    .col-dm-value  { width: 80px; }
  }
}
</style>
